/**
 * Πωλήσεις (migration 285, Φάση 1 — 3/11/2026): τύποι, ελληνικά ονόματα και οι καθαροί υπολογισμοί των οθονών
 * («Σήμερα», γρήγορα κουμπιά). Οι κανόνες σταδίου ζουν στο backend (services/salesLeads.js)· εδώ μόνο ό,τι
 * χρειάζεται για να μη σταλεί κάτι που θα γυρίσει 400.
 */
import type { ChipTone } from '../shared/listPage';
import type { StorePrefill } from '../superadmin/SuperAdminStoresTab';

export type LeadStatus = 'new' | 'contacted' | 'meeting' | 'proposal' | 'negotiation' | 'won' | 'lost' | 'later';

export interface Lead {
  id: number;
  external_key: string | null;
  name: string;
  track: 'single' | 'chain';
  kind: string;
  business_stage: string | null;
  area: string | null;
  address: string | null;
  stores_count: number | null;
  stores_note: string | null;
  platforms: string[];
  phone: string | null;
  phone_note: string | null;
  email: string | null;
  website: string | null;
  contact_name: string | null;
  current_system: string | null;
  contract_end: string | null;
  signals: string[];
  why_now: string | null;
  opener: string | null;
  fit_score: number | null;
  research_sources: string[];
  source: string;
  referred_by_store_id: number | null;
  referred_by_note: string | null;
  status: LeadStatus;
  owner_id: number | null;
  owner_name: string | null;
  next_action: string | null;
  next_action_at: string | null;
  lost_reason: string | null;
  lost_note: string | null;
  retry_at: string | null;
  won_store_id: number | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
  last_activity_at: string | null;
  activity_count: number;
  /** Χάρτης (migration 286): σημείο από τη γεωκωδικοποίηση· 'none' = δεν βρέθηκε (να μην ξαναψάχνεται). */
  lat?: number | null;
  lng?: number | null;
  geo_precision?: 'address' | 'area' | 'manual' | 'none' | null;
  /** Migration 287: εκτίμηση του χρήστη σε €/μήνα (η PostgreSQL τη στέλνει ως κείμενο, π.χ. "49.90"). */
  expected_monthly?: number | string | null;
}

export interface LeadActivity {
  id: number;
  lead_id: number;
  type: string;
  outcome: string | null;
  body: string | null;
  from_status: string | null;
  to_status: string | null;
  occurred_at: string;
  created_by: number | null;
  author_name: string | null;
}

export interface SalesMeta {
  owners: Array<{ id: number; name: string | null; role: string }>;
  stores: Array<{ id: number; name: string }>;
  me: number;
}

/** Τα πεδία που γράφει η φόρμα (ό,τι στέλνεται στο POST/PUT). */
export type LeadInput = Partial<Pick<Lead,
  'name' | 'track' | 'kind' | 'business_stage' | 'area' | 'address' | 'stores_count' | 'stores_note' | 'platforms' | 'phone'
  | 'phone_note' | 'email' | 'website' | 'contact_name' | 'current_system' | 'contract_end' | 'signals' | 'why_now' | 'opener'
  | 'fit_score' | 'source' | 'referred_by_store_id' | 'referred_by_note' | 'status' | 'owner_id' | 'next_action'
  | 'next_action_at' | 'lost_reason' | 'lost_note' | 'retry_at' | 'won_store_id' | 'notes' | 'expected_monthly'>>;

/** Η αξία (€/μήνα) ως αριθμός· 0 όταν δεν έχει εκτιμηθεί. */
export const leadValue = (l: Pick<Lead, 'expected_monthly'>): number => {
  const n = Number(l.expected_monthly);
  return l.expected_monthly === null || l.expected_monthly === undefined || l.expected_monthly === '' || !Number.isFinite(n) ? 0 : n;
};
/** «1.250 €/μήνα» — στρογγυλό, όπως στις αναφορές (εκτίμηση, όχι λογαριασμός). */
export const formatMonthly = (n: number): string => `${Math.round(n).toLocaleString('el-GR')} €/μήνα`;

type Option = { value: string; label: string };

export const STATUS_OPTIONS: Array<Option & { value: LeadStatus; tone: ChipTone }> = [
  { value: 'new', label: 'Νέος', tone: 'brand' },
  { value: 'contacted', label: 'Επαφή', tone: 'muted' },
  { value: 'meeting', label: 'Ραντεβού / Demo', tone: 'warn' },
  { value: 'proposal', label: 'Προσφορά', tone: 'warn' },
  { value: 'negotiation', label: 'Διαπραγμάτευση', tone: 'warn' },
  { value: 'won', label: 'Πελάτης', tone: 'ok' },
  { value: 'lost', label: 'Χάθηκε', tone: 'danger' },
  { value: 'later', label: 'Αργότερα', tone: 'muted' },
];
export const ACTIVE_STATUSES: LeadStatus[] = ['new', 'contacted', 'meeting', 'proposal', 'negotiation'];
/** Μετά την πρώτη επαφή, ενεργός = πάντα ημερομηνία επόμενου βήματος (ίδιος κανόνας με το backend). */
export const NEEDS_NEXT: LeadStatus[] = ['contacted', 'meeting', 'proposal', 'negotiation'];

export const KIND_OPTIONS: Option[] = [
  { value: 'delivery', label: 'Delivery / take away' },
  { value: 'cafe', label: 'Καφέ / σνακ / κουλούρι' },
  { value: 'restaurant', label: 'Εστιατόριο με τραπέζια' },
  { value: 'bakery', label: 'Φούρνος / ζαχαροπλαστείο' },
  { value: 'other', label: 'Άλλο / άγνωστο' },
];
export const TRACK_OPTIONS: Option[] = [
  { value: 'single', label: 'Μεμονωμένο' },
  { value: 'chain', label: 'Αλυσίδα' },
];
export const BUSINESS_STAGE_OPTIONS: Option[] = [
  { value: 'opening', label: 'Ανοίγει τώρα' },
  { value: 'existing', label: 'Λειτουργεί ήδη' },
];
export const SOURCE_OPTIONS: Option[] = [
  { value: 'referral', label: 'Σύσταση' },
  { value: 'site_demo', label: 'Site · demo' },
  { value: 'site_contact', label: 'Site · φόρμα' },
  { value: 'research', label: 'Έρευνα' },
  { value: 'gemi', label: 'ΓΕΜΗ' },
  { value: 'walk_in', label: 'Επίσκεψη' },
  { value: 'other', label: 'Άλλο' },
];
export const LOST_REASON_OPTIONS: Option[] = [
  { value: 'price', label: 'Τιμή' },
  { value: 'has_system', label: 'Έχει σύστημα / συμβόλαιο' },
  { value: 'not_decider', label: 'Δεν αποφασίζει ο συνομιλητής' },
  { value: 'missing_feature', label: 'Λείπει λειτουργία' },
  { value: 'no_response', label: 'Δεν απάντησε ποτέ' },
  { value: 'timing', label: 'Κακός χρόνος' },
  { value: 'competitor', label: 'Πήγε σε ανταγωνιστή' },
  { value: 'closed', label: 'Έκλεισε / δεν άνοιξε' },
  { value: 'other', label: 'Άλλο' },
];
export const PLATFORM_OPTIONS: Option[] = [
  { value: 'efood', label: 'efood' },
  { value: 'wolt', label: 'Wolt' },
  { value: 'box', label: 'BOX' },
];
export const SIGNAL_OPTIONS: Option[] = [
  { value: 'new_opening', label: 'Ανοίγει τώρα' },
  { value: 'multi_platform', label: 'Σε 2+ πλατφόρμες' },
  { value: 'expanding', label: 'Επεκτείνεται' },
  { value: 'order_issues', label: 'Παράπονα για παραγγελίες' },
  { value: 'dine_in_heavy', label: 'Πολλά τραπέζια' },
  { value: 'weighed_products', label: 'Ζυγιζόμενα' },
];
export const ACTIVITY_TYPE_OPTIONS: Option[] = [
  { value: 'call', label: 'Κλήση' },
  { value: 'visit', label: 'Επίσκεψη' },
  { value: 'meeting', label: 'Συνάντηση' },
  { value: 'demo', label: 'Demo' },
  { value: 'email', label: 'Email' },
  { value: 'note', label: 'Σημείωση' },
];
const ACTIVITY_LABELS: Record<string, string> = {
  ...Object.fromEntries(ACTIVITY_TYPE_OPTIONS.map(o => [o.value, o.label])),
  status: 'Στάδιο', site_form: 'Φόρμα site', import: 'Εισαγωγή',
};
const OUTCOME_LABELS: Record<string, string> = {
  no_answer: 'δεν απάντησε', talked: 'μιλήσαμε', callback: 'να ξαναπάρουμε', meeting_set: 'κλείσαμε ραντεβού',
};

const labelOf = (list: Option[], value: string | null | undefined): string =>
  (value ? list.find(o => o.value === value)?.label || value : '—');
export const statusLabel = (s: string | null | undefined) => labelOf(STATUS_OPTIONS, s);
export const statusTone = (s: string): ChipTone => STATUS_OPTIONS.find(o => o.value === s)?.tone || 'muted';
export const kindLabel = (k: string | null | undefined) => labelOf(KIND_OPTIONS, k);
export const sourceLabel = (s: string | null | undefined) => labelOf(SOURCE_OPTIONS, s);
export const lostReasonLabel = (r: string | null | undefined) => labelOf(LOST_REASON_OPTIONS, r);

/** «Κλήση · δεν απάντησε», «Στάδιο: Επαφή → Χάθηκε». */
export function activityTitle(a: LeadActivity): string {
  if (a.type === 'status') {
    return a.from_status
      ? `Στάδιο: ${statusLabel(a.from_status)} → ${statusLabel(a.to_status)}`
      : `Στάδιο: ${statusLabel(a.to_status)}`;
  }
  const base = ACTIVITY_LABELS[a.type] || a.type;
  return a.outcome ? `${base} · ${OUTCOME_LABELS[a.outcome] || a.outcome}` : base;
}

/** 'YYYY-MM-DD' + n ημέρες (ημερολογιακές, χωρίς ώρα — καμία μετατόπιση από ζώνη ώρας). */
export function addDays(ymd: string, n: number): string {
  const d = new Date(`${ymd}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}

/** «σήμερα», «αύριο», «χθες», «σε 3 ημ.», «πριν 5 ημ.» */
export function relativeDay(ymd: string, today: string): string {
  const diff = Math.round((Date.parse(`${ymd}T00:00:00Z`) - Date.parse(`${today}T00:00:00Z`)) / 86400000);
  if (diff === 0) return 'σήμερα';
  if (diff === 1) return 'αύριο';
  if (diff === -1) return 'χθες';
  return diff > 0 ? `σε ${diff} ημ.` : `πριν ${-diff} ημ.`;
}

export interface TodayBuckets {
  /** Επόμενο βήμα που πέρασε. */
  overdue: Lead[];
  /** Επόμενο βήμα σήμερα. */
  today: Lead[];
  /** «Νέοι» χωρίς επόμενο βήμα — π.χ. ήρθαν από το site και δεν τους μίλησε κανείς. */
  untouched: Lead[];
  /** Χαμένοι / «αργότερα» που έφτασε η μέρα να ξαναδοκιμάσουμε. */
  retry: Lead[];
  /** Τις επόμενες 7 ημέρες. */
  upcoming: Lead[];
}

/** Η οθόνη «Σήμερα». `ownerId` = μόνο οι δικοί του (και όσοι δεν έχουν υπεύθυνο). */
export function todayBuckets(leads: Lead[], today: string, ownerId?: number | null): TodayBuckets {
  const mine = ownerId ? leads.filter(l => l.owner_id === ownerId || l.owner_id === null) : leads;
  const byDate = (a: Lead, b: Lead) => (a.next_action_at || '').localeCompare(b.next_action_at || '') || a.id - b.id;
  const active = mine.filter(l => ACTIVE_STATUSES.includes(l.status));
  const week = addDays(today, 7);
  return {
    overdue: active.filter(l => l.next_action_at && l.next_action_at < today).sort(byDate),
    today: active.filter(l => l.next_action_at === today).sort(byDate),
    untouched: active.filter(l => l.status === 'new' && !l.next_action_at)
      .sort((a, b) => b.created_at.localeCompare(a.created_at)),
    retry: mine.filter(l => (l.status === 'lost' || l.status === 'later') && l.retry_at && l.retry_at <= today)
      .sort((a, b) => (a.retry_at || '').localeCompare(b.retry_at || '')),
    upcoming: active.filter(l => l.next_action_at && l.next_action_at > today && l.next_action_at <= week).sort(byDate),
  };
}

export type QuickAction = 'no_answer' | 'callback';

/**
 * Τα δύο γρήγορα κουμπιά του πιλότου: «Δεν απάντησε» (ξανά αύριο) και «Ξαναπάρε» (σε 3 ημέρες). Κλήση στο
 * ιστορικό + επόμενο βήμα· ο «Νέος» γίνεται «Επαφή», τα άλλα στάδια μένουν.
 */
export function quickActionPayload(lead: Lead, action: QuickAction, today: string) {
  const days = action === 'no_answer' ? 1 : 3;
  const status: LeadStatus = lead.status === 'new' ? 'contacted' : lead.status;
  const keepsStatus = !ACTIVE_STATUSES.includes(status);
  return {
    activity: { type: 'call', outcome: action },
    lead: keepsStatus ? {} : {
      status,
      next_action: action === 'no_answer' ? 'Ξανά τηλέφωνο (δεν απάντησε)' : 'Ξανά τηλέφωνο',
      next_action_at: addDays(today, days),
    } as LeadInput,
  };
}

// ── Χάρτης ──────────────────────────────────────────────────────────────────
/** Χρώμα σημείου ανά στάδιο (ίδια λογική με τις ετικέτες: μπλε νέος, πορτοκαλί σε εξέλιξη, πράσινο, κόκκινο). */
export const STATUS_COLORS: Record<LeadStatus, string> = {
  new: '#2563EB', contacted: '#64748B', meeting: '#F59E0B', proposal: '#F97316', negotiation: '#EA580C',
  won: '#16A34A', lost: '#DC2626', later: '#94A3B8',
};

/** Τι ψάχνει ο Geocoder: η διεύθυνση (ακριβές) ή μόνο η περιοχή (κατά προσέγγιση). null = τίποτα να ψάξει. */
export function geocodeQuery(l: Pick<Lead, 'address' | 'area'>): { query: string; precision: 'address' | 'area' } | null {
  const address = (l.address || '').trim();
  const area = (l.area || '').trim();
  if (address) return { query: [address, area, 'Ελλάδα'].filter(Boolean).join(', '), precision: 'address' };
  if (area) return { query: `${area}, Ελλάδα`, precision: 'area' };
  return null;
}

// ── Διπλοί ──────────────────────────────────────────────────────────────────
const accentless = (s: string) => s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLocaleLowerCase('el')
  .replace(/[^\p{L}\p{N}]+/gu, ' ').trim();
/** Τα 10 τελευταία ψηφία (χωρίς +30/0030 και κενά) — «210 1234567» = «+302101234567». */
const phoneKey = (p: string | null | undefined) => {
  const d = (p || '').replace(/\D/g, '');
  return d.length >= 10 ? d.slice(-10) : '';
};

export interface DuplicateMatch { lead: Lead; reasons: string[] }

/** Υπάρχοντες υποψήφιοι με ίδιο τηλέφωνο, email ή επωνυμία (χωρίς τόνους/κεφαλαία). */
export function findDuplicates(draft: Pick<LeadInput, 'name' | 'phone' | 'email'>, leads: Lead[], excludeId?: number | null): DuplicateMatch[] {
  const name = draft.name ? accentless(draft.name) : '';
  const phone = phoneKey(draft.phone);
  const email = (draft.email || '').trim().toLowerCase();
  const out: DuplicateMatch[] = [];
  for (const l of leads) {
    if (l.id === excludeId) continue;
    const reasons: string[] = [];
    if (phone && phoneKey(l.phone) === phone) reasons.push('ίδιο τηλέφωνο');
    if (email && (l.email || '').trim().toLowerCase() === email) reasons.push('ίδιο email');
    if (name.length >= 3 && accentless(l.name) === name) reasons.push('ίδια επωνυμία');
    if (reasons.length) out.push({ lead: l, reasons });
  }
  return out.slice(0, 5);
}

// ── «Έγινε πελάτης» → «Νέο κατάστημα» ──────────────────────────────────────
/**
 * Όνομα/επώνυμο από τον «συνομιλητή». Η έρευνα τον γράφει «Όνομα Επώνυμο — ρόλος (πηγή)»: κρατιέται μόνο το όνομα.
 */
export function contactPerson(contact: string | null | undefined): { first_name: string; last_name: string } {
  const clean = (contact || '').split(/\s+[—–-]\s+|\(|,/)[0].trim();
  const [first, ...rest] = clean.split(/\s+/).filter(Boolean);
  return { first_name: first || '', last_name: rest.join(' ') };
}

/**
 * Η φόρμα «Νέο κατάστημα» του super admin, συμπληρωμένη από τον υποψήφιο: επωνυμία, διεύθυνση (κείμενο — τα
 * ακριβή πεδία έρχονται όταν διαλεχτεί από την αναζήτηση της φόρμας), περιοχή, τηλέφωνο, email, συνομιλητής ως
 * διευθυντής. Τίποτα από χρήματα: ούτε η «αξία» περνά στις Χρεώσεις.
 */
export function storePrefillFromLead(lead: Lead, onCreated: StorePrefill['onCreated']): StorePrefill {
  return {
    nonce: Date.now(),
    store: {
      name: lead.name, address: lead.address || '', area: lead.area || '', phone_number: lead.phone || '', email: lead.email || '',
    },
    manager: { ...contactPerson(lead.contact_name), email: lead.email || '', phone: lead.phone || '' },
    onCreated,
  };
}

/** Τι λείπει για να αποθηκευτεί το στάδιο — το ίδιο μήνυμα με το backend, πριν φύγει το αίτημα. */
export function stageProblem(v: LeadInput): string | null {
  if (!v.name || !String(v.name).trim()) return 'Λείπει η επωνυμία';
  if (v.status === 'lost' && !v.lost_reason) return 'Γράψε γιατί χάθηκε';
  if (v.status === 'later' && !v.retry_at) return 'Βάλε πότε να ξαναδοκιμάσουμε';
  if (v.status && NEEDS_NEXT.includes(v.status) && !v.next_action_at) return 'Βάλε ημερομηνία για το επόμενο βήμα';
  return null;
}
